import React from 'react';
import { Button, Input, message, Modal, Segmented, Tag } from 'antd';
import { useMutation } from '@tanstack/react-query';
import type { ConnectEnvironment } from '../../services/connectFeedback';
import { getPlusAccess, PlusAccessStatus, setPlusAccess } from '../../services/connectPlusAccess';

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error || fallback;

/**
 * Looks up a Connect app user by email and grants or revokes Ourora Plus for
 * their couple, so test accounts get Plus without a store purchase. Revoke only
 * removes admin-granted Plus; store purchases are untouched.
 */
const PlusAccessPanel: React.FC = () => {
  const [environment, setEnvironment] = React.useState<ConnectEnvironment>('dev');
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState<PlusAccessStatus | null>(null);

  const lookup = useMutation({
    mutationFn: () => getPlusAccess(environment, email.trim()),
    onSuccess: setStatus,
    onError: (error) => {
      setStatus(null);
      message.error(errorMessage(error, 'Failed to look up user'));
    },
  });

  const change = useMutation({
    mutationFn: (active: boolean) =>
      setPlusAccess({ env: environment, email: status!.email, active }),
    onSuccess: (next, active) => {
      setStatus(next);
      if (next.hasPlus === active) {
        message.success(active ? 'Plus granted' : 'Plus revoked');
      } else {
        message.warning('No change: the couple has Plus from a purchase or another grant');
      }
    },
    onError: (error) => message.error(errorMessage(error, 'Failed to update Plus')),
  });

  const confirmChange = (active: boolean) => {
    const run = () => change.mutate(active);
    if (environment !== 'prod') return run();
    Modal.confirm({
      title: `${active ? 'Grant' : 'Revoke'} Plus in production?`,
      content: `This changes real access for ${status!.email} and their partner.`,
      okText: active ? 'Grant' : 'Revoke',
      okButtonProps: { danger: !active },
      onOk: run,
    });
  };

  const switchEnvironment = (value: ConnectEnvironment) => {
    setEnvironment(value);
    setStatus(null);
  };

  return (
    <section className="admin-plus-access" aria-label="Ourora Plus access">
      <div className="admin-directory-toolbar">
        <div className="admin-directory-heading">
          <h2>Ourora Plus access</h2>
          <span>Give test accounts Plus without a store purchase</span>
        </div>
        <Segmented
          value={environment}
          onChange={(value) => switchEnvironment(value as ConnectEnvironment)}
          options={[
            { label: 'Dev Server', value: 'dev' },
            { label: 'Production', value: 'prod' },
          ]}
        />
      </div>

      <div className="admin-plus-access-body">
        <Input.Search
          placeholder="App user email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setStatus(null);
          }}
          onSearch={() => email.trim() && lookup.mutate()}
          enterButton="Look up"
          loading={lookup.isPending}
        />

        {status && (
          <div className="admin-plus-access-result">
            <div className="admin-plus-access-status">
              <strong>{status.email}</strong>
              {!status.isPaired ? (
                <Tag bordered={false}>Not paired</Tag>
              ) : status.hasPlus ? (
                <Tag bordered={false} color="green">
                  {status.adminGranted ? 'Plus (granted here)' : 'Plus (purchase or other grant)'}
                </Tag>
              ) : (
                <Tag bordered={false}>No Plus</Tag>
              )}
            </div>
            {status.isPaired && (
              <div className="admin-plus-access-actions">
                {!status.hasPlus && (
                  <Button type="primary" loading={change.isPending} onClick={() => confirmChange(true)}>
                    Grant Plus
                  </Button>
                )}
                {status.adminGranted && (
                  <Button danger loading={change.isPending} onClick={() => confirmChange(false)}>
                    Revoke Plus
                  </Button>
                )}
              </div>
            )}
            {!status.isPaired && (
              <span className="admin-plus-access-note">Plus belongs to a couple, so pair this account first.</span>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PlusAccessPanel;
